import { Banner, DataTable, Text } from '@dixa/lib-shared-habla';
import { ReadSearchApiArg } from 'lib-search-types';
import { ampli } from 'lib-shared-tracking';
import { User } from 'lib-shared-types-api';
import { useEffect, useMemo } from 'react';

import { searchApi, useReadSearchInfiniteQuery } from 'apis/search';
import { createSearchUrlParams } from 'app/views/layout/views/search/utils';
import { AppLink } from 'components/appLink';

import styles from '../../userDetails.module.scss';
import { makeColumns } from '../../utils/makeColumns';

export type UserConversationsProps = {
  user: User;
};

export function UserConversations({
  user,
}: UserConversationsProps): JSX.Element {
  const searchArg: ReadSearchApiArg<'conversations', true> = useMemo(
    () => ({
      scope: 'conversations',
      filters: {
        strategy: 'all',
        filters: [
          {
            field: 'requested_by',
            operator: 'is_one',
            values: [{ value: user.id }],
          },
        ],
      },
      enrichmentSpec: ['assigned_to'],
      pageSize: 6,
    }),
    [user.id],
  );

  const {
    results,
    meta,
    isFetching,
    isError,
    hasNextPage,
    hasPreviousPage,
    fetchNextPage,
    fetchPreviousPage,
  } = useReadSearchInfiniteQuery(searchArg, { infinite: false });

  const [readAllConversationsTotal, { allTotal, isFetchingAllTotal }] =
    searchApi.useLazyReadSelectiveSearchQuery({
      selectFromResult: ({ data, isFetching }) => ({
        allTotal: data?.meta.total_results ?? 0,
        isFetchingAllTotal: isFetching,
      }),
    });

  /**
   * Fetches the total count of all conversations (including those in restricted
   * queues). The diff between this and `accessibleTotal` gives the number of
   * conversations excluded due to queue restrictions.
   */
  useEffect(() => {
    readAllConversationsTotal({
      scope: 'conversations',
      filters: searchArg.filters,
      selectFields: ['id'],
      urlParams: {
        include_restricted: true,
      },
    });
  }, [readAllConversationsTotal, searchArg.filters]);

  const accessibleTotal = meta?.total_results ?? 0;
  const restrictedTotal =
    !isFetchingAllTotal && allTotal > accessibleTotal
      ? allTotal - accessibleTotal
      : 0;

  if (isError) {
    return (
      <Banner
        variant="fail"
        icon={null}
        description={
          <Text.Paragraph>
            An error occurred while loading conversations
          </Text.Paragraph>
        }
      />
    );
  }

  if (!results.length) {
    if (isFetching) {
      return <Text.Paragraph>Loading conversations...</Text.Paragraph>;
    }

    return (
      <Banner
        variant="info"
        icon={null}
        description={
          <Text.Paragraph>There are no previous conversations</Text.Paragraph>
        }
      />
    );
  }

  return (
    <>
      {!isFetchingAllTotal && restrictedTotal > 0 ? (
        <Banner
          variant="warning"
          description={
            <Text.Paragraph>{`${restrictedTotal} conversations in restricted queues excluded`}</Text.Paragraph>
          }
          className={styles.restrictedHitsBanner}
        />
      ) : null}

      <DataTable
        data={results}
        columns={makeColumns(
          (conversation) => conversation.assigned_to,
          'Assigned to',
        )}
        paginationProps={{
          loading: isFetching,
          previousButtonDisabled: !hasPreviousPage,
          nextButtonDisabled: !hasNextPage,
          previousButtonClick: fetchPreviousPage,
          nextButtonClick: fetchNextPage,
        }}
      />

      <div className={styles.seeAllLinkContainer}>
        <AppLink
          path="/search/:scope"
          params={createSearchUrlParams({
            scope: 'conversations',
            filters: {
              strategy: 'all',
              filters: [
                {
                  field: 'requested_by',
                  operator: 'is_one',
                  values: [{ value: user.id }],
                },
              ],
            },
          })}
          onClick={() => ampli.contactsCardSeeAllConversationsClicked()}
        >
          {`See all ${accessibleTotal ? `${accessibleTotal} ` : ''}${
            restrictedTotal > 0 ? 'unrestricted ' : ''
          }conversations`}
        </AppLink>
      </div>
    </>
  );
}
